import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, ArrowRight, MapPin, User, Leaf, Star, ShoppingCart, Info, Award, Quote } from 'lucide-react';

const GrapesCatalogue = () => {
  const [selectedVariety, setSelectedVariety] = useState(null);

  const grapeVarieties = [
    {
      id: 'sonaka',
      name: 'Sonaka Grapes',
      subtitle: 'Premium Seedless White Grapes',
      image: 'https://images.pexels.com/photos/9968757/pexels-photo-9968757.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹180-220',
      unit: 'per kg',
      minOrder: '100 kg',
      season: 'January - April',
      origin: 'Nashik, Maharashtra',
      organic: true,
      exportGrade: true,
      grade: 'Export Grade A',
      brix: '18-22°',
      berrySize: '18-22mm',
      description: 'Premium Sonaka grapes known for their exceptional sweetness, crisp texture, and beautiful golden-green color. Seedless variety perfect for table consumption and gifting. India\'s most exported grape variety.',
      features: ['Seedless', 'High Brix content', 'Crisp texture', 'Long shelf life', 'Export quality', 'Cold-chain compatible'],
      farmer: {
        name: 'Ramesh Kisan Patil',
        location: 'Dindori, Nashik',
        image: 'https://images.unsplash.com/photo-1608366911553-3296e6f5e2ef?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400',
        experience: '28 years',
        farmSize: '32 acres',
        story: 'For years, I struggled with middlemen who paid us barely enough to cover costs. Three seasons of crop loss due to unseasonal rains almost broke us. When SPANXPORT approached us in 2019, I was skeptical. But they helped us get GlobalGAP certified, trained our workers on grading, and now pay us 40% more than the mandi rate. My son, who had left for the city, is back on the farm. This is not just better income - it\'s our dignity restored.'
      },
      certifications: ['APEDA Certified', 'GlobalGAP', 'Organic India'],
      exportMarkets: ['UAE', 'UK', 'Netherlands', 'Germany']
    },
    {
      id: 'thompson',
      name: 'Thompson Seedless',
      subtitle: 'Classic Green Seedless Grapes',
      image: 'https://images.pexels.com/photos/27590169/pexels-photo-27590169.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹140-180',
      unit: 'per kg',
      minOrder: '100 kg',
      season: 'February - May',
      origin: 'Sangli, Maharashtra',
      organic: false,
      exportGrade: true,
      grade: 'Grade A',
      brix: '16-20°',
      berrySize: '16-20mm',
      description: 'The classic Thompson Seedless - loved worldwide for its balanced sweetness and versatility. Excellent for fresh consumption, raisins, and wine production. Consistent quality year after year.',
      features: ['Seedless', 'Versatile use', 'Balanced sweetness', 'Processing suitable', 'Year-round demand'],
      farmer: {
        name: 'Suresh Baburao Jadhav',
        location: 'Tasgaon, Sangli',
        image: 'https://images.pexels.com/photos/29912210/pexels-photo-29912210.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '22 years',
        farmSize: '18 acres',
        story: 'I remember selling my grapes at ₹25/kg in the mandi after waiting for hours in the sun, while the same grapes sold for ₹200 in Dubai supermarkets. SPANXPORT showed us that gap and helped us bridge it. They provided cold storage access, export packaging, and most importantly - advance payment before harvest. My daughter is now studying agriculture in Pune. The cycle of poverty is finally breaking.'
      },
      certifications: ['APEDA Certified', 'FSSAI'],
      exportMarkets: ['Bangladesh', 'UAE', 'Saudi Arabia']
    },
    {
      id: 'black-grapes',
      name: 'Black Grapes (Sharad)',
      subtitle: 'Premium Black Seedless',
      image: 'https://images.pexels.com/photos/760281/pexels-photo-760281.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹200-260',
      unit: 'per kg',
      minOrder: '50 kg',
      season: 'January - March',
      origin: 'Nashik, Maharashtra',
      organic: true,
      exportGrade: true,
      grade: 'Premium Export',
      brix: '20-24°',
      berrySize: '20-24mm',
      description: 'Deep purple-black grapes with intense sweetness and antioxidant-rich flesh. Sharad Seedless is known for its exceptional taste, firm texture, and beautiful bloom. Premium variety for discerning markets.',
      features: ['Antioxidant rich', 'Intense flavor', 'Firm texture', 'Beautiful bloom', 'Gift quality', 'Premium pricing'],
      farmer: {
        name: 'Vitthal Pandurang Shinde',
        location: 'Niphad, Nashik',
        image: 'https://images.unsplash.com/photo-1709532918611-77db9df9b27d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400',
        experience: '30 years',
        farmSize: '45 acres',
        story: 'Black grapes need extra care - they show every blemish. I lost entire harvests to hailstorms twice. The bank loans were crushing us. SPANXPORT not only bought our damaged crop at fair prices but also helped us install hail nets with subsidized financing. Now my black grapes go to London and Dubai. Last Diwali, I bought my wife gold - something I couldn\'t afford for 20 years of marriage.'
      },
      certifications: ['GlobalGAP', 'Organic India', 'APEDA'],
      exportMarkets: ['UK', 'Germany', 'UAE', 'Singapore']
    },
    {
      id: 'crimson',
      name: 'Crimson Seedless',
      subtitle: 'Late-Season Red Grapes',
      image: 'https://images.pexels.com/photos/3629537/pexels-photo-3629537.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹160-200',
      unit: 'per kg',
      minOrder: '100 kg',
      season: 'March - June',
      origin: 'Solapur, Maharashtra',
      organic: false,
      exportGrade: true,
      grade: 'Export Grade A',
      brix: '17-21°',
      berrySize: '18-22mm',
      description: 'Beautiful red-pink grapes with crisp bite and excellent shelf life. Crimson Seedless is the go-to variety for late season exports when other varieties have finished. Consistent demand in European markets.',
      features: ['Extended season', 'Long shelf life', 'Crisp texture', 'Beautiful color', 'EU market favorite'],
      farmer: {
        name: 'Dnyaneshwar Mahadeo Kale',
        location: 'Pandharpur, Solapur',
        image: 'https://images.pexels.com/photos/3339871/pexels-photo-3339871.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '18 years',
        farmSize: '22 acres',
        story: 'Solapur is hot and dry - people said grapes won\'t survive here. But Crimson variety thrives in our climate. The challenge was always marketing - no cold chains, no export contacts. SPANXPORT built a collection center in our village, trained 40 farmers including women, and now we export directly. My village that once saw farmer suicides now has prosperity. This partnership saved lives, not just livelihoods.'
      },
      certifications: ['APEDA', 'GlobalGAP'],
      exportMarkets: ['Netherlands', 'UK', 'Belgium']
    },
    {
      id: 'flame',
      name: 'Flame Seedless',
      subtitle: 'Early-Season Red Grapes',
      image: 'https://images.pexels.com/photos/890507/pexels-photo-890507.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹150-190',
      unit: 'per kg',
      minOrder: '100 kg',
      season: 'December - February',
      origin: 'Pune, Maharashtra',
      organic: true,
      exportGrade: true,
      grade: 'Grade A',
      brix: '16-19°',
      berrySize: '16-18mm',
      description: 'Bright red early-season grapes that hit the market when demand is highest. Flame Seedless offers sweet, juicy berries with characteristic crunch. Perfect for winter fruit baskets and holiday gifting.',
      features: ['Early harvest', 'Bright red color', 'Sweet & juicy', 'Holiday demand', 'Premium timing'],
      farmer: {
        name: 'Shantabai Tanaji Pawar',
        location: 'Junnar, Pune',
        image: 'https://images.unsplash.com/photo-1709207513502-d9242031ab1b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400',
        experience: '15 years',
        farmSize: '12 acres',
        story: 'After my husband passed away, everyone said a woman cannot run a vineyard. The workers wouldn\'t listen, the traders cheated me. SPANXPORT was the first to treat me as an equal farmer. They trained me on modern techniques, helped me get organic certification, and my Flame grapes now fetch premium prices in UAE during Christmas. Today, I employ 15 women from my village. We are proof that women farmers can succeed.'
      },
      certifications: ['Organic India', 'APEDA', 'Women Farmer Collective'],
      exportMarkets: ['UAE', 'Saudi Arabia', 'Kuwait']
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-[450px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/442116/pexels-photo-442116.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Indian grape vineyards"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#263776]/95 via-[#263776]/80 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <Link to="/products/orivana-fresh" className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to ORIVANA Fresh
          </Link>
          <Badge className="bg-green-500 text-white mb-4 block w-fit">
            <Leaf className="w-3 h-3 mr-1 inline" /> Your Single Stop for Premium Grapes
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Indian Table Grapes</h1>
          <p className="text-lg md:text-xl text-white/90 max-w-2xl">
            Export-quality grapes from Maharashtra's finest vineyards. Direct from farmers, quality assured, transparency guaranteed.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <Badge className="bg-white/20 text-white px-4 py-2">5 Premium Varieties</Badge>
            <Badge className="bg-white/20 text-white px-4 py-2">GlobalGAP Certified</Badge>
            <Badge className="bg-white/20 text-white px-4 py-2">Cold-Chain Delivery</Badge>
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="bg-gradient-to-r from-green-600 to-green-700 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-6 md:gap-12 text-white text-sm flex-wrap">
            <span className="flex items-center"><Award className="w-4 h-4 mr-2" /> India's #1 Grape Exporter Region</span>
            <span className="hidden md:inline">|</span>
            <span>Quality & Transparency Assured</span>
            <span className="hidden md:inline">|</span>
            <span>Fair Prices to Farmers</span>
          </div>
        </div>
      </section>

      {/* Varieties Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#263776] mb-4">Select Your Variety</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Each variety is carefully grown by our partner farmers who receive fair prices and support for sustainable farming
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Varieties List */}
            <div className="lg:col-span-1">
              <div className="space-y-4">
                {grapeVarieties.map((variety) => (
                  <Card 
                    key={variety.id}
                    className={`cursor-pointer transition-all duration-300 ${
                      selectedVariety?.id === variety.id 
                        ? 'ring-2 ring-green-500 shadow-lg bg-green-50' 
                        : 'hover:shadow-md hover:bg-slate-50'
                    }`}
                    onClick={() => setSelectedVariety(variety)}
                    data-testid={`grape-variety-${variety.id}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <img 
                        src={variety.image} 
                        alt={variety.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-[#263776]">{variety.name}</h3>
                          {variety.exportGrade && (
                            <Badge className="bg-green-100 text-green-700 text-xs">Export</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{variety.origin}</p>
                        <p className="text-green-600 font-bold mt-1">{variety.price} <span className="text-xs text-slate-400">{variety.unit}</span></p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Right: Selected Variety Details */}
            <div className="lg:col-span-2">
              {selectedVariety ? (
                <div className="sticky top-4">
                  <div className="relative rounded-xl overflow-hidden h-[350px] mb-6">
                    <img 
                      src={selectedVariety.image} 
                      alt={selectedVariety.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                      <Badge className="bg-[#263776] text-white">{selectedVariety.grade}</Badge>
                      {selectedVariety.exportGrade && (
                        <Badge className="bg-green-600 text-white">
                          <Award className="w-3 h-3 mr-1" /> Export Grade
                        </Badge>
                      )}
                      {selectedVariety.organic && (
                        <Badge className="bg-emerald-600 text-white">
                          <Leaf className="w-3 h-3 mr-1" /> Organic
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h2 className="text-3xl font-bold text-[#263776] mb-2">{selectedVariety.name}</h2>
                      <p className="text-lg text-green-600 font-medium mb-4">{selectedVariety.subtitle}</p>
                      <p className="text-slate-600 mb-4">{selectedVariety.description}</p>
                      
                      <div className="bg-slate-50 rounded-lg p-4 mb-4">
                        <h4 className="font-bold text-[#263776] mb-3 flex items-center">
                          <Info className="w-4 h-4 mr-2" /> Specifications
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-slate-500">Season:</span> <span className="font-medium">{selectedVariety.season}</span></div>
                          <div><span className="text-slate-500">Origin:</span> <span className="font-medium">{selectedVariety.origin}</span></div>
                          <div><span className="text-slate-500">Brix:</span> <span className="font-medium">{selectedVariety.brix}</span></div>
                          <div><span className="text-slate-500">Berry Size:</span> <span className="font-medium">{selectedVariety.berrySize}</span></div>
                          <div><span className="text-slate-500">Min Order:</span> <span className="font-medium">{selectedVariety.minOrder}</span></div>
                          <div><span className="text-slate-500">Grade:</span> <span className="font-medium">{selectedVariety.grade}</span></div>
                        </div>
                      </div>

                      {selectedVariety.exportMarkets && (
                        <div className="mb-4">
                          <h4 className="font-bold text-[#263776] mb-2">Export Markets</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedVariety.exportMarkets.map((market, idx) => (
                              <Badge key={idx} className="bg-blue-100 text-blue-700">
                                {market}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mb-4">
                        <h4 className="font-bold text-[#263776] mb-2">Key Features</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedVariety.features.map((feature, idx) => (
                            <Badge key={idx} variant="outline" className="border-[#263776]/30">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-bold text-[#263776] mb-2">Certifications</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedVariety.certifications.map((cert, idx) => (
                            <Badge key={idx} className="bg-[#263776]/10 text-[#263776]">
                              {cert}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <Card className="mb-6 border-2 border-green-200 bg-green-50/50">
                        <CardContent className="p-6">
                          <div className="text-3xl font-bold text-green-700 mb-1">{selectedVariety.price}</div>
                          <div className="text-slate-500 mb-4">{selectedVariety.unit} (Wholesale/Export)</div>
                          <div className="text-sm text-slate-600 mb-4">
                            Minimum Order: <span className="font-bold">{selectedVariety.minOrder}</span>
                          </div>
                          <Button asChild className="w-full bg-green-600 hover:bg-green-700 mb-3">
                            <Link to={`/products/orivana-fresh/enquiry?product=${selectedVariety.id}&category=grapes`}>
                              <ShoppingCart className="w-4 h-4 mr-2" />
                              Request Quote
                            </Link>
                          </Button>
                          <Button asChild variant="outline" className="w-full border-[#263776] text-[#263776]">
                            <Link to="/contact">
                              Contact Sales Team
                            </Link>
                          </Button>
                        </CardContent>
                      </Card>

                      {/* Farmer Story Card */}
                      <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                        <CardContent className="p-6">
                          <h4 className="font-bold text-[#263776] mb-4 flex items-center">
                            <User className="w-5 h-5 mr-2 text-amber-600" /> Meet the Farmer
                          </h4>
                          <div className="flex items-start gap-4 mb-4">
                            <img 
                              src={selectedVariety.farmer.image}
                              alt={selectedVariety.farmer.name}
                              className="w-20 h-20 rounded-full object-cover border-2 border-amber-300"
                            />
                            <div>
                              <h5 className="font-bold text-[#263776]">{selectedVariety.farmer.name}</h5>
                              <div className="flex items-center text-sm text-slate-500 mb-1">
                                <MapPin className="w-3 h-3 mr-1" />
                                {selectedVariety.farmer.location}
                              </div>
                              <div className="text-xs text-slate-400">
                                {selectedVariety.farmer.experience} experience • {selectedVariety.farmer.farmSize}
                              </div>
                            </div>
                          </div>
                          <div className="relative">
                            <Quote className="w-6 h-6 text-amber-300 absolute -top-2 -left-1" />
                            <p className="text-sm text-slate-700 pl-6 italic leading-relaxed">
                              {selectedVariety.farmer.story}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[500px] bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-dashed border-green-200">
                  <div className="text-center p-8">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Leaf className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-[#263776] mb-2">Select a Variety</h3>
                    <p className="text-slate-500 max-w-sm">Click on any grape variety to view detailed specifications, farmer story, and pricing</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 bg-[#263776]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Why Source Grapes from SPANXPORT?</h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              Your single stop for every grape need - quality assured, transparency guaranteed
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { title: 'Direct Sourcing', desc: 'From 500+ verified farmers in Maharashtra' },
              { title: 'Quality Control', desc: '6-point inspection at farm, pack house & dispatch' },
              { title: 'Fair Trade', desc: 'Farmers receive 30-40% above mandi prices' },
              { title: 'Full Traceability', desc: 'Farm-to-fork tracking with QR codes' }
            ].map((item, idx) => (
              <Card key={idx} className="bg-white/10 border-0 text-center p-6">
                <h4 className="font-bold text-white mb-2">{item.title}</h4>
                <p className="text-sm text-white/70">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gradient-to-r from-green-600 to-green-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Place Your Order?</h2>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">
            Get competitive quotes for bulk orders. We handle export documentation, cold-chain logistics, and customs clearance.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-white text-green-700 hover:bg-green-50">
              <Link to="/products/orivana-fresh/enquiry?category=grapes">Get Export Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <Link to="/verified-directory/apply-farmer">Partner as Farmer</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GrapesCatalogue;
